// src/pages/PlayersPage.js
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import './PlayersPage.css';

import TopBar from '../components/TopBar';
import FilterBar from '../components/FilterBar';
import PlayerCard from '../components/PlayerCard';
import PlayerCompareAI from '../components/PlayerCompareAI';

import {
  getPlayersFromFirestore,
  syncAllFantasyData,
} from '../firebase/playerService';

import { applyFantAppRoleOverrides } from '../utils/fantAppRoleOverrides';
import { DEFAULT_MANTRA_ROLE_CONFIGURATION } from '../components/auction/MantraRoleConfiguration';
import { buildRoleOverrideMap } from '../components/auction/auctionRosterUtils';

import {
  SearchCheck,
  Scale,
} from 'lucide-react';

function getLast5Score(player) {
  const l5 = player.last5 || {};

  return (
    Number(l5.markavg || 0) * 0.45 +
    Number(l5.fmarkavg || 0) * 0.35 +
    Number(l5.goals || 0) * 0.1 +
    Number(l5.assists || 0) * 0.1
  );
}

function getGoalsAgainst(player) {
  return Number(
    player.gotgoals ??
    player.goalsConceded ??
    player.goals_against ??
    0
  );
}

function getSaves(player) {
  return Number(
    player.spenalties ??
    player.penaltiesSaved ??
    player.penalties_saved ??
    player.saves ??
    0
  );
}

export default function PlayersPage() {
  const [players, setPlayers] = useState([]);

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('value_desc');
  const [role, setRole] = useState('all');
  const [team, setTeam] = useState('all');

  const [loading, setLoading] = useState(true);

  const [compareIds, setCompareIds] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);

  // Ruoli Mantra per nome giocatore (DEFAULT_MANTRA_ROLE_CONFIGURATION): stringa
  // tipo "Dc/Ds" indicizzata sul nome normalizzato. I portieri non sono nel
  // config → fallback a "P" nella card.
  const mantraRoleMap = useMemo(
    () => buildRoleOverrideMap({ roleOverrides: DEFAULT_MANTRA_ROLE_CONFIGURATION }),
    []
  );

  const handleCloseCompare = useCallback(() => {
    setCompareOpen(false);
    setCompareIds([]);
    document.body.style.overflow = '';
  }, []);

  useEffect(() => {
    (async () => {
      let list = await getPlayersFromFirestore();

      if (!list.length) {
        await syncAllFantasyData();
        list = await getPlayersFromFirestore();
      }

      setPlayers(applyFantAppRoleOverrides(list));
      setLoading(false);
    })().catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (compareIds.length < 2) {
      setCompareOpen(false);
    }
    if (!compareOpen) {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [compareIds]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        handleCloseCompare();
      }
    };

    window.addEventListener('keydown', onKey);

    return () => window.removeEventListener('keydown', onKey);
  }, [handleCloseCompare]);

  const filtered = useMemo(() => {
    let list = [...players];

    if (search.trim()) {
      const s = search.toLowerCase().trim();

      list = list.filter((p) =>
        `${p.name || ''} ${p.team || ''} ${p.role || ''}`
          .toLowerCase()
          .includes(s)
      );
    }

    if (role !== 'all') {
      list = list.filter(
        (p) => String(p.role || '').trim() === String(role)
      );
    }

    if (team !== 'all') {
      list = list.filter(
        (p) => String(p.team || '').trim() === String(team)
      );
    }

    const sortMap = {
      value_desc: (a, b) =>
        Number(b.value || 0) - Number(a.value || 0),

      caps_desc: (a, b) =>
        Number(b.caps || 0) - Number(a.caps || 0),

      goals_desc: (a, b) =>
        Number(b.goals || 0) - Number(a.goals || 0),

      goals_against_desc: (a, b) =>
        getGoalsAgainst(b) - getGoalsAgainst(a),

      assists_desc: (a, b) =>
        Number(b.assists || 0) - Number(a.assists || 0),

      saves_desc: (a, b) =>
        getSaves(b) - getSaves(a),

      markavg_desc: (a, b) =>
        Number(b.markavg || 0) - Number(a.markavg || 0),

      fmarkavg_desc: (a, b) =>
        Number(b.fmarkavg || 0) - Number(a.fmarkavg || 0),

      last5_desc: (a, b) =>
        getLast5Score(b) - getLast5Score(a),

      ycards_desc: (a, b) =>
        Number(b.ycards || 0) - Number(a.ycards || 0),

      rcards_desc: (a, b) =>
        Number(b.rcards || 0) - Number(a.rcards || 0),

      owngoals_desc: (a, b) =>
        Number(b.owngoals || 0) - Number(a.owngoals || 0),

      mpenalties_desc: (a, b) =>
        Number(b.mpenalties || 0) - Number(a.mpenalties || 0),

      name_asc: (a, b) =>
        String(a.name || '').localeCompare(String(b.name || '')),
    };

    list.sort(sortMap[sort] || sortMap.value_desc);

    return list;
  }, [players, search, role, team, sort]);

  const comparePlayers = useMemo(
    () =>
      compareIds
        .map((id) =>
          players.find((p) => String(p.id) === String(id))
        )
        .filter(Boolean),
    [compareIds, players]
  );

  const clearFilters = () => {
    setSearch('');
    setSort('value_desc');
    setRole('all');
    setTeam('all');
    setCompareIds([]);
  };

  const toggleCompare = (player) => {
    setCompareIds((current) => {
      const id = String(player.id);

      return current.includes(id)
        ? current.filter((x) => x !== id)
        : [...current, id].slice(-4);
    });
  };

  return (
    <main className="container players-page">
      <TopBar />

      <section className="players-hero glass">
        <div>
          <div className="players-hero__eyebrow">
            <SearchCheck size={16} />
            Player browser
          </div>
          <h1>Lista giocatori</h1>
          <p>
            Ricerca live, filtri avanzati e confronto giocatori.
          </p>
        </div>
      </section>

      <FilterBar
        players={players}
        search={search}
        setSearch={setSearch}
        sort={sort}
        setSort={setSort}
        role={role}
        setRole={setRole}
        team={team}
        setTeam={setTeam}
        onClear={clearFilters}
      />

      {/* FLOATING COMPARE BUTTON */}

      {comparePlayers.length > 0 && (
        <button
          className="players-floatingCompare"
          onClick={() => setCompareOpen(true)}
        >
          <Scale size={18} />

          <span>
            Confronta ({comparePlayers.length})
          </span>
        </button>
      )}

      {/* OVERLAY */}
      {compareOpen && (
        <div
          className="players-compareOverlay"
          onClick={handleCloseCompare}
        >
          <div
            className="players-compareModal cai-scrollbar"
            onClick={(e) => e.stopPropagation()}
          >
            <PlayerCompareAI
              players={comparePlayers}
              onClose={handleCloseCompare}
            />
          </div>
        </div>
      )}

      <div className="players-grid">
        {loading ? (
          <div className="players-empty glass">
            Caricamento giocatori...
          </div>
        ) : filtered.length ? (
          filtered.map((player) => (
            <PlayerCard
              key={player.id}
              player={player}
              allPlayers={players}
              mantraRoleMap={mantraRoleMap}
              selected={compareIds.includes(
                String(player.id)
              )}
              onToggleCompare={toggleCompare}
              sortKey={sort}
            />
          ))
        ) : (
          <div className="players-empty glass">
            Nessun giocatore trovato.
          </div>
        )}
      </div>
    </main>
  );
}